// Curl-noise flow field — the engine of motion. Sampling the curl of
// a 3D simplex noise gives a divergence-free vector field, so bubbles
// never accumulate or fall into a stable point. The other steering
// terms (UI avoid, cursor flee, pair separate, orbit bias) layer on
// top of this in bubble-forces.ts.

import { createNoise3D } from 'simplex-noise';
import type { Bubble, Vec2 } from './bubble-physics';
import { FLOW_FORCE, NOISE_SCALE, NOISE_TIME_SCALE } from './bubble-constants';
import { steerToward } from './bubble-forces';

const noise3D = createNoise3D();
const EPS = 1.5;

function curlAt(x: number, y: number, t: number): Vec2 {
  const dN_dy =
    (noise3D(x * NOISE_SCALE, (y + EPS) * NOISE_SCALE, t) -
      noise3D(x * NOISE_SCALE, (y - EPS) * NOISE_SCALE, t)) /
    (2 * EPS);
  const dN_dx =
    (noise3D((x + EPS) * NOISE_SCALE, y * NOISE_SCALE, t) -
      noise3D((x - EPS) * NOISE_SCALE, y * NOISE_SCALE, t)) /
    (2 * EPS);
  return { x: dN_dy, y: -dN_dx };
}

export function flowFollow(b: Bubble, ctx: { time: number }): Vec2 {
  const c = curlAt(b.pos.x, b.pos.y, ctx.time * NOISE_TIME_SCALE);
  const len = Math.hypot(c.x, c.y) || 1;
  const desired = {
    x: (c.x / len) * b.maxSpeed,
    y: (c.y / len) * b.maxSpeed,
  };
  return steerToward(desired, b.vel, FLOW_FORCE);
}
